import { Box, Fade, Popover, popoverClasses } from '@mui/material';
import { styled } from '@mui/material/styles';
import { IconInfoRedesigned } from 'assets/img';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getColorForAvailabilityType } from '../helpers/get-color-for-availability-type';

export function AvailabilityInfoButton() {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { t } = useTranslation('all-platforms-common');

  const closeTimer = useRef<number | null>(null);

  const legendItems = [
    { type: 'available', label: t('availability_page.worker_available') } as const,
    { type: 'pending', label: t('availability_page.worker_pending') } as const,
    { type: 'tentative', label: t('availability_page.worker_tentative') } as const,
    { type: 'blocked', label: t('availability_page.worker_blocked') } as const,
  ];

  const startHoverEffect = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setAnchorEl(e.currentTarget);
  };

  const stopHoverEffect = () => {
    closeTimer.current = window.setTimeout(() => {
      setAnchorEl(null);
    }, 100);
  };

  const finalOpen = Boolean(anchorEl);

  return (
    <>
      <StyledBox
        aria-label={t('availability_page.info')}
        onMouseEnter={startHoverEffect}
        onMouseLeave={stopHoverEffect}
      >
        <StyledIconInfoRedesigned />
      </StyledBox>
      <StyledPopover
        open={finalOpen}
        sx={{ pointerEvents: 'none' }}
        anchorEl={anchorEl}
        onClose={undefined}
        disableEnforceFocus={true}
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 0 }}
        disableRestoreFocus
        disableAutoFocus={true}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              marginLeft: '-8px',
            },
          },
        }}
        PaperProps={{
          sx: {
            width: 300,
            maxHeight: 400,
            overflow: 'hidden',
            borderRadius: 3,
            boxShadow: '0 10px 30px rgba(32, 45, 77, 0.12), 0 6px 12px rgba(32,45,77,0.06)',
          },
        }}
      >
        <Box>
          {legendItems.map((item) => (
            <Box key={item.type} display="flex" alignItems="center" mb={1}>
              <Box
                width={12}
                height={12}
                borderRadius="50%"
                bgcolor={getColorForAvailabilityType(item.type, 0.5)}
                mr={3}
              />
              <StyledLabel>{item.label}</StyledLabel>
            </Box>
          ))}
        </Box>
      </StyledPopover>
    </>
  );
}

const StyledPopover = styled(Popover)(({ theme }) => ({
  lineHeight: theme.spacing(4.5),
  [`& .${popoverClasses.paper}`]: {
    boxShadow: theme.shadows[3],
    borderRadius: theme.spacing(1.5),
    maxHeight: theme.spacing(80),
    maxWidth: theme.spacing(80),
    padding: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
  },
}));

const StyledBox = styled(Box)({
  marginLeft: 'auto',
});

const StyledIconInfoRedesigned = styled(IconInfoRedesigned)({});

const StyledLabel = styled('span')(({ theme }) => ({
  fontSize: theme.typography.pxToRem(13),
  color: theme.palette.text.primary,
  fontWeight: theme.typography.fontWeightBold,
  fontFamily: theme.typography.body2.fontFamily,
}));
